import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/staff/ImageUpload";
import { api, errMsg } from "@/lib/api";

const EMPTY = { code: "", name: "", address: "", coords: "", bank_name: "", account_number: "", account_holder: "", qris_merchant: "", qris_image: "", active: true };
const FIELDS = [
  ["name", "Nama gerai"], ["address", "Alamat"], ["coords", "Titik koordinat (lat, lng)"],
  ["bank_name", "Nama bank"], ["account_number", "Nomor rekening"], ["account_holder", "Atas nama"], ["qris_merchant", "Nama merchant QRIS"],
];
const input = "w-full px-3 py-2 rounded-lg border border-sage-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

export const OutletManager = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(null);

  const load = useCallback(() => { api.get("/admin/outlets").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { id, ...body } = f;
      if (id) await api.put(`/admin/outlets/${id}`, body);
      else await api.post("/admin/outlets", body);
      toast.success("Gerai disimpan");
      setF(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="card-paper p-6" data-testid="outlet-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif font-semibold text-2xl">Gerai</h2>
        <button onClick={() => setF(EMPTY)} className="btn-forest px-3 py-2 text-sm flex items-center gap-1.5" data-testid="outlet-add-button"><Plus className="w-4 h-4" /> Tambah gerai</button>
      </div>
      <p className="text-xs text-muted2 mt-1">Tiap gerai punya kode, alamat, rekening & QRIS sendiri. Kode dipakai di nomor pesanan.</p>
      <div className="divide-y divide-sage-200 mt-3">
        {list.map((o) => (
          <div key={o.id} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid={`outlet-row-${o.code}`}>
            <div><div className="font-semibold">{o.code} · {o.name} {!o.active && <span className="text-xs text-red-700">(nonaktif)</span>}</div><div className="text-xs text-muted2">{o.address || "-"} · {o.bank_name} {o.account_number}</div></div>
            <button onClick={() => setF(o)} className="p-2 rounded-lg hover:bg-sage-100" data-testid={`outlet-edit-button-${o.code}`}><Pencil className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!f} onOpenChange={(v) => !v && setF(null)}>
        <DialogContent className="bg-paper max-w-md max-h-[90vh] overflow-y-auto" data-testid="outlet-form-dialog">
          <DialogHeader><DialogTitle>{f?.id ? `Ubah gerai ${f.code}` : "Tambah gerai"}</DialogTitle></DialogHeader>
          {f && (
            <form onSubmit={save} className="space-y-2.5">
              <input required disabled={!!f.id} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} maxLength={10} placeholder="Kode gerai (mis. KD)" className={input} data-testid="outlet-code-input" />
              {FIELDS.map(([k, label]) => (
                <input key={k} required={["name", "bank_name", "account_number", "account_holder"].includes(k)} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={label} className={input} data-testid={`outlet-${k}-input`} />
              ))}
              <div className="text-sm"><span className="text-muted2 block mb-1">Gambar QRIS</span><ImageUpload square value={f.qris_image} onChange={(v) => setF({ ...f, qris_image: v })} testid="outlet-qris-image-input" /></div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="outlet-active-switch" /> Aktif</label>
              <button className="btn-forest w-full py-2.5" data-testid="outlet-save-button">Simpan gerai</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
